import React, { useMemo, useState } from 'react';

import { Control, Controller } from 'react-hook-form';
import debounce from 'debounce-promise';

import SelectCreatable from 'components/SelectCreatable';
import { preQualificationApi } from 'prequalificationV2/api/PreQualification/PreQualificationApi';
import { convertInputSelectValue } from 'prequalificationV2/utils/inputSelect';
import { DEFAULT_DEBOUNCE_TIMEOUT } from 'prequalificationV2/utils/constants';

import { EMPLOYER_NAME_LABEL } from '../borrowerIncomeInfo.constants';
import styles from '../BorrowerIncomeInfo.module.scss';
import { EmployerCompanyOption, IncomeFormValues } from '../borrowerIncomeInfo.types';

interface EmployerCompanySelectProps {
  control: Control<IncomeFormValues>;
  index: number;
  disabled?: boolean;
}

const EmployerCompanySelect = ({ control, index, disabled }: EmployerCompanySelectProps) => {
  const employerCompanySelectName = `incomes.${index}.employerCompany` as const;
  const [companySearchInput, setCompanySearchInput] = useState('');

  const loadEmployerCompanies = useMemo(() => {
    return debounce(async (employerCompanyName: string) => {
      if (!employerCompanyName.trim().length) {
        return [];
      }

      try {
        const employerCompaniesResponse = await preQualificationApi.getCompanies(employerCompanyName);

        return employerCompaniesResponse.companies.map((company) => {
          return {
            label: company.companyName,
            value: convertInputSelectValue(company.companyName),
            iconUrl: company.logoUrl,
            payrollProviderMappingId: company.companyMappingId,
          } as EmployerCompanyOption;
        });
      } catch {
        return [];
      }
    }, DEFAULT_DEBOUNCE_TIMEOUT);
  }, []);

  return (
    <Controller
      name={employerCompanySelectName}
      control={control}
      rules={{
        validate: (value) => Boolean((value as EmployerCompanyOption | null)?.label) || 'Employer Name is required',
      }}
      render={({ field, fieldState }) => (
        <SelectCreatable
          name={employerCompanySelectName}
          isClearable
          isOptionWithIcon
          isDisabled={disabled}
          loadOptions={loadEmployerCompanies}
          label={EMPLOYER_NAME_LABEL}
          onChange={(selectedCompanyOptionOrOptions) => {
            const selectedOption = selectedCompanyOptionOrOptions as EmployerCompanyOption | null;

            field.onChange(
              selectedOption
                ? {
                    label: selectedOption.label,
                    value: selectedOption.value,
                    iconUrl: selectedOption.iconUrl,
                    payrollProviderMappingId: selectedOption.__isNew__
                      ? null
                      : selectedOption.payrollProviderMappingId || null,
                  }
                : null,
            );
          }}
          onInputChange={setCompanySearchInput}
          value={(field.value as EmployerCompanyOption | null) || undefined}
          inputValue={companySearchInput}
          inputContainerClassname={styles.input}
          errorMessage={fieldState.error?.message}
          isValidNewOption={(nextCompanySearchInputValue) => Boolean(nextCompanySearchInputValue.length)}
          formatCreateLabel={(nextCompanySearchInputValue) => (
            <>I cannot find my company in the list. Add: {nextCompanySearchInputValue}</>
          )}
        />
      )}
    />
  );
};

export default EmployerCompanySelect;
